import {put, call} from 'redux-saga/effects';
import Role from "../../entities/role";
import * as Actions from "../actions/roleActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type RoleAction = {
    roleId: string;
    role: Role;
    roles: Role[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneRole(action: RoleAction) {
	const session = tabSession.captureGeneration();
	try {
		const role: Role = yield call(api.findOne, 'role', action.roleId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ROLE_FETCH_ONE_SUCCESS, roleId: action.roleId, role});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ROLE_FETCH_ONE_FAILURE, roleId: action.roleId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {RoleAction} action
 */
export function* saveRole(action: RoleAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ ROLE_SAVE saga', action.role?.id);
    
	if (action.role === undefined || action.role === null) {
		console.error('saveRole received a null');
		throw new Error('saveRole received a null');
	}

	if (!(action.role instanceof Role)) {
		action.role = new Role(action.role);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'role', action.role);
		if (addResult.isErr()) {
			console.error(`⮂ saveRole: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.ROLE_SAVE_FAILURE, role: action.role, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ROLE_SAVE, role: action.role});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.ROLE_SAVE_FAILURE, role: action.role, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {RoleAction} action
 * @param {Role[]} action.roles
 */
export function* saveManyRoles(action: RoleAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ ROLE_SAVE_MANY saga', action.roles.map(role => role.id).join(','));
    
    if (!Array.isArray(action.roles)) {
        console.error('saveManyRoles expected an array, received', action.roles);
        throw new Error('saveManyRoles expected an array');
    }

	try {
		const toSave: Role[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.roles.length; i++) {
			const role = action.roles[i];
			role.persisted = false;
			toSave.push(role);
		}

		const addResult = yield call(idb.add, 'role', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyRoles: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.ROLE_SAVE_MANY_FAILURE, roles: action.roles, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ROLE_SAVE_MANY, roles: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.ROLE_SAVE_MANY_FAILURE, roles: action.roles, status: 500, err});

		console.error(`Error saving ${action.roles.length} roles to database`, err);
	}
}

/**
 * @param {RoleAction} action
 */
export function* removeRole(action: RoleAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.role === undefined || action.role === null || !(action.role instanceof Role)) {
			console.error('removeRole expected type Role, received', action.role);
			throw new Error('removeRole expected type Role');
		}

	try {
		// Mark as deleted
		action.role.status = 'D';
		action.role.addedOn = newHRTimestamp();
		action.role.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'role', action.role);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeRole: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ROLE_REMOVE_FAILURE, role: action.role, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ROLE_REMOVE, role: action.role});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ROLE_REMOVE_FAILURE, role: action.role, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Role.
	*
	* @param {RoleAction} action
	*/
export function* restoreRole(action: RoleAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreRole expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.role === undefined || action.role === null) {
        console.error('restoreRole expected a Role, received null');
        throw new Error('restoreRole expected a Role, received null');
    }
    else if (!(action.role instanceof Role)) action.role = new Role(action.role); 
    
	try {
		// Mark as restored
		action.role.status = 'U';
		action.role.addedOn = newHRTimestamp();
		action.role.addedBy = userId;
		action.role.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'role', action.role);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreRole: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ROLE_RESTORE_FAILURE, role: action.role, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ROLE_RESTORE, role: action.role});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ROLE_RESTORE_FAILURE, role: action.role, status: 500, err});

		console.error('Error restoring from database', err);
	}
}